import { ChallengeArtifactsSection } from '@/components/artifacts';
import { ChallengeCollectiblesSection } from '@/components/collectibles';
import type { TemplateChallengeLink } from '@/config/objects/templates';
import { getOrdinal } from '@/lib/utils';
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  FormFieldGroup,
  Input,
  FormPageLayout,
  Skeleton,
  Switch,
  toast,
  useApiClient,
} from '@startupquickstart/frontend';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Save } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

/** Extended link type including library challenge details from the GET endpoint */
interface TemplateChallengeLinkDetail extends TemplateChallengeLink {
  libraryChallenge: {
    id: string;
    name: string;
    description?: string | null;
    clue?: string;
    hint?: string | null;
    type: string;
    difficulty: string;
    points: number;
    zone?: string | null;
    latitude?: number | null;
    longitude?: number | null;
    requiresPhoto?: boolean;
    tags?: string[];
    imageUrl?: string | null;
  };
  group?: {
    id: string;
    name: string;
    color?: string | null;
  } | null;
}

interface SettingsFormData {
  isStarter: boolean;
  requireAll: boolean;
  maxCompletions: string;
  speedBonuses: string[];
}

const DEFAULT_FORM: SettingsFormData = {
  isStarter: false,
  requireAll: true,
  maxCompletions: '',
  speedBonuses: [],
};

/**
 * Form page for editing template-level challenge settings.
 * These settings can be overridden per-event when the template is instantiated.
 */
export function TemplateChallengeSettingsPage() {
  const { id: templateId, linkId } = useParams<{ id: string; linkId: string }>();
  const navigate = useNavigate();
  const apiClient = useApiClient();
  const queryClient = useQueryClient();

  const [formData, setFormData] = useState<SettingsFormData>(DEFAULT_FORM);
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const [formReady, setFormReady] = useState(false);

  const updateField = useCallback(
    <K extends keyof SettingsFormData>(key: K, value: SettingsFormData[K]) => {
      setFormData((prev) => ({ ...prev, [key]: value }));
    },
    [],
  );

  // Fetch the challenge link
  const {
    data: linkResponse,
    isLoading,
    isError,
  } = useQuery<{ success: boolean; data: TemplateChallengeLinkDetail }>({
    queryKey: ['TemplateChallengeLink', templateId, linkId],
    queryFn: () => apiClient.get(`/templates/${templateId}/challenges/${linkId}`),
    enabled: !!templateId && !!linkId,
  });

  useEffect(() => {
    if (!linkResponse?.data) return;
    const link = linkResponse.data;
    setFormData({
      isStarter: link.isStarter ?? false,
      requireAll: link.requireAll ?? true,
      maxCompletions: link.maxCompletions?.toString() ?? '',
      speedBonuses: (link.speedBonuses ?? []).map(String),
    });
    setLastSaved(new Date(link.updatedAt));
    setFormReady(true);
  }, [linkResponse]);

  useEffect(() => {
    if (isError) {
      toast.error('Challenge not found');
      navigate(`/templates/${templateId}/challenges`);
    }
  }, [isError, navigate, templateId]);

  const preparePayload = () => ({
    isStarter: formData.isStarter,
    requireAll: formData.requireAll,
    maxCompletions: formData.maxCompletions ? parseInt(formData.maxCompletions) : null,
    speedBonuses: formData.speedBonuses
      .map((v) => parseInt(v))
      .filter((v) => !isNaN(v) && v > 0),
  });

  const saveMutation = useMutation({
    mutationFn: async (payload: Record<string, unknown>) => {
      return apiClient.patch(`/templates/${templateId}/challenges/${linkId}`, payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['TemplateChallengeLink', templateId, linkId] });
      queryClient.invalidateQueries({ queryKey: ['TemplateChallenges', templateId] });
      queryClient.invalidateQueries({ queryKey: ['TemplateGroups', templateId] });
      setLastSaved(new Date());
      toast.success('Settings saved');
      navigate(`/templates/${templateId}/challenges`);
    },
    onError: (err: Error) => {
      toast.error(err?.message || 'Failed to save settings');
    },
  });

  const handleSave = () => {
    const payload = preparePayload();
    if (payload.maxCompletions !== null && (isNaN(payload.maxCompletions) || payload.maxCompletions < 1)) {
      toast.error('Max completions must be a positive number or empty for unlimited');
      return;
    }
    saveMutation.mutate(payload);
  };

  const handleCancel = () => {
    navigate(`/templates/${templateId}/challenges`);
  };

  const link = linkResponse?.data;

  if (isLoading || !formReady) {
    return (
      <FormPageLayout>
        <FormPageLayout.Header>
          <div className="flex items-center gap-4">
            <Skeleton className="h-9 w-9 rounded-lg" />
            <div className="space-y-2">
              <Skeleton className="h-6 w-40" />
              <Skeleton className="h-4 w-60" />
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Skeleton className="h-9 w-20" />
            <Skeleton className="h-9 w-32" />
          </div>
        </FormPageLayout.Header>
        <FormPageLayout.Content>
          {[1, 2].map((i) => (
            <Card key={i} className="rounded-xl border bg-card shadow-sm">
              <CardHeader className="px-6 py-4 !pb-4 border-b">
                <Skeleton className="h-5 w-40" />
                <Skeleton className="h-4 w-64" />
              </CardHeader>
              <CardContent className="p-6 space-y-4">
                <Skeleton className="h-10 w-full" />
                <Skeleton className="h-10 w-full" />
              </CardContent>
            </Card>
          ))}
        </FormPageLayout.Content>
      </FormPageLayout>
    );
  }

  return (
    <FormPageLayout>
      {/* Header */}
      <FormPageLayout.Header>
        <div className="flex items-center gap-4">
          <button
            type="button"
            onClick={handleCancel}
            className="flex items-center justify-center h-9 w-9 rounded-lg border bg-card hover:bg-muted transition-colors cursor-pointer"
          >
            <ArrowLeft className="h-4 w-4" />
          </button>
          <div>
            <h1 className="text-xl font-semibold text-foreground">Challenge Settings</h1>
            <p className="text-sm text-muted-foreground">
              {link?.libraryChallenge.name ?? 'Configure template-level settings'}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Button type="button" variant="ghost" onClick={handleCancel}>
            Cancel
          </Button>
          <Button type="button" onClick={handleSave} loading={saveMutation.isPending} icon={Save}>
            Save Settings
          </Button>
        </div>
      </FormPageLayout.Header>

      {/* Content */}
      <FormPageLayout.Content>
        <div className="space-y-6">
          {/* Challenge Info (read-only summary) */}
          <Card className="rounded-xl border bg-card shadow-sm py-0 gap-0">
            <CardHeader className="px-6 py-4 !pb-4 border-b">
              <CardTitle>Challenge</CardTitle>
              <CardDescription>
                Settings from the library challenge. Edit the challenge itself from the{' '}
                <button
                  type="button"
                  onClick={() => navigate(`/challenges/${link?.libraryChallenge.id}`)}
                  className="text-primary hover:underline"
                >
                  challenge library
                </button>.
              </CardDescription>
            </CardHeader>
            <CardContent className="p-6">
              <div className="grid gap-4 sm:grid-cols-3">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Type</p>
                  <p className="text-sm capitalize">{link?.libraryChallenge.type}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Difficulty</p>
                  <p className="text-sm capitalize">{link?.libraryChallenge.difficulty}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Points</p>
                  <p className="text-sm">{link?.libraryChallenge.points}</p>
                </div>
              </div>
              {link?.group && (
                <div className="mt-4 pt-4 border-t">
                  <p className="text-sm font-medium text-muted-foreground">Group</p>
                  <p className="text-sm">{link.group.name}</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Template Settings */}
          <Card className="rounded-xl border bg-card shadow-sm py-0 gap-0">
            <CardHeader className="px-6 py-4 !pb-4 border-b">
              <CardTitle>Template Settings</CardTitle>
              <CardDescription>
                These settings apply when the template is used to create an event.
                They can be overridden per-event.
              </CardDescription>
            </CardHeader>
            <CardContent className="p-6 space-y-6">
              <FormFieldGroup
                name="maxCompletions"
                label="Max Completing Teams"
                description="Challenge disappears after this many teams complete it. Leave empty for unlimited."
              >
                <Input
                  id="maxCompletions"
                  type="number"
                  min={1}
                  value={formData.maxCompletions}
                  onChange={(e) => updateField('maxCompletions', e.target.value)}
                  placeholder="Unlimited"
                />
              </FormFieldGroup>

              <div className="space-y-2">
                <p className="text-sm font-medium">Speed Bonus</p>
                <p className="text-sm text-muted-foreground">
                  Bonus points awarded to teams based on their finishing position.
                </p>
                <div className="space-y-2">
                  {formData.speedBonuses.map((value, index) => (
                    <div key={index} className="flex items-center gap-2">
                      <FormFieldGroup name={`speedBonus-${index}`} label={`${getOrdinal(index + 1)} Place`} className="flex-1">
                        <Input
                          type="number"
                          min={0}
                          value={value}
                          onChange={(e) => {
                            const updated = [...formData.speedBonuses];
                            updated[index] = e.target.value;
                            updateField('speedBonuses', updated);
                          }}
                          placeholder="0"
                        />
                      </FormFieldGroup>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="mt-5 h-9 w-9 p-0 text-muted-foreground hover:text-destructive"
                        onClick={() => {
                          const updated = formData.speedBonuses.filter((_, i) => i !== index);
                          updateField('speedBonuses', updated);
                        }}
                      >
                        &times;
                      </Button>
                    </div>
                  ))}
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => updateField('speedBonuses', [...formData.speedBonuses, ''])}
                  >
                    Add Tier
                  </Button>
                </div>
              </div>

              <div className="flex items-center justify-between rounded-lg border p-4">
                <div className="space-y-0.5">
                  <p className="text-sm font-medium">Starter Challenge</p>
                  <p className="text-sm text-muted-foreground">
                    Available immediately when the event starts
                  </p>
                </div>
                <Switch
                  checked={formData.isStarter}
                  onCheckedChange={(checked) => updateField('isStarter', checked)}
                />
              </div>

              <div className="flex items-center justify-between rounded-lg border p-4">
                <div className="space-y-0.5">
                  <p className="text-sm font-medium">Require All Prerequisites</p>
                  <p className="text-sm text-muted-foreground">
                    All prerequisites must be completed (vs. any one)
                  </p>
                </div>
                <Switch
                  checked={formData.requireAll}
                  onCheckedChange={(checked) => updateField('requireAll', checked)}
                />
              </div>
            </CardContent>
          </Card>

          {/* Rewards — collectible awards/requirements + artifact rewards.
              Standalone mode: each card owns its Save button, mirroring how
              the event challenge editor surfaces the same sections. The
              template challenge LINK id plays the challengeId role. */}
          {templateId && linkId && (
            <>
              <ChallengeCollectiblesSection
                ownerId={templateId}
                apiBase={`/templates/${templateId}`}
                keyPrefix="Template"
                challengeId={linkId}
              />
              <ChallengeArtifactsSection
                ownerId={templateId}
                apiBase={`/templates/${templateId}`}
                keyPrefix="Template"
                challengeId={linkId}
              />
            </>
          )}
        </div>
      </FormPageLayout.Content>

      {/* Footer */}
      <FormPageLayout.Footer lastSaved={lastSaved}>
        <div className="flex items-center gap-3">
          <Button type="button" variant="ghost" onClick={handleCancel}>
            Cancel
          </Button>
          <Button type="button" onClick={handleSave} loading={saveMutation.isPending} icon={Save}>
            Save Settings
          </Button>
        </div>
      </FormPageLayout.Footer>
    </FormPageLayout>
  );
}
